import { extend, isObject } from "@dhx/ts-common/core";
import { awaitRedraw, VNode } from "@dhx/ts-common/dom";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { IKeyManager, KeyManager } from "@dhx/ts-common/KeyManager";
import { View } from "@dhx/ts-common/view";
import {
	DataEvents,
	Diagram,
	DiagramEvents,
	flowShapes,
	SelectionEvents,
	IShapeToolbarConfig,
	ActionDirection,
} from "@dhx/ts-diagram";
import { Layout } from "@dhx/ts-layout";
import { IDataItem } from "@dhx/ts-data";

import { Controls } from "./controls/Controls";
import { drag } from "./helpers/drag";
import { Sidebar } from "./Sidebar";
import { HistoryManager } from "./HistoryManager";
import { SelectionBox } from "./SelectionBox";
import { BlockSelection } from "./BlockSelection";
import {
	DiagramEditorEvents,
	IDefaultEditorConfig,
	IDiagramEditor,
	IDiagramEditorHandlersMap,
	IEditorConfig,
	IMindmapEditorConfig,
	IOrgEditorConfig,
	ISelectionBox,
	ViewMode,
} from "./types";
import { orgShapeToolbar, defaultShapeToolbar, mindmapShapeToolbar } from "./configs/toolbar";
import { Id } from "@dhx/ts-common/types";
import { topbar } from "./helpers/topbar";

// taken from package.json
declare let VERSION: string;

export abstract class BaseDiagramEditor extends View implements IDiagramEditor {
	public version: string;
	public config: IEditorConfig;
	public events: IEventSystem<
		DataEvents | SelectionEvents | DiagramEvents | DiagramEditorEvents,
		IDiagramEditorHandlersMap
	>;
	public diagram: Diagram;
	public history: HistoryManager;

	protected _layout: Layout;
	protected _sidebar: Sidebar;

	protected _resizer: Controls;
	protected _selectionBox: SelectionBox;
	protected _blockSelection: BlockSelection;
	protected _keyManager: IKeyManager;

	constructor(container: string | HTMLElement, config: IEditorConfig) {
		let defaultShapeType;
		switch (config.type) {
			case "mindmap":
				defaultShapeType = "topic";
				break;
			case "org":
				defaultShapeType = "card";
				break;
			default:
				defaultShapeType = "rectangle";
				break;
		}

		const cfg = extend(
			{
				shapeType: defaultShapeType,
				gridStep: 10,
				reservedWidth: 40,
				editMode: true,
				lineGap: 20,
				defaults: {},
				scale: 1,
				gapPreview: "6px 8px",
				scalePreview: 0.5,
				shapeBarWidth: 295,
				controls: {
					apply: true,
					reset: true,
					export: true,
					import: true,
					autoLayout: true,
					historyManager: true,
					editManager: true,
					scale: true,
					gridStep: true,
				},
				shapeToolbar: true,
				type: "default",
			},
			config
		);

		super(container, cfg);

		this.version = VERSION;
		this.events = new EventSystem<
			DataEvents | SelectionEvents | DiagramEvents | DiagramEditorEvents,
			IDiagramEditorHandlersMap
		>(this);

		this._initDiagram();

		this.history = new HistoryManager(this.diagram.data);

		this._initUI(container);
		this._keyManager = new KeyManager((_, focusId) => focusId === (this.diagram as any)._uid);
		this._setHandlers();
	}
	public paint(): void {
		this._layout.paint();
		this.diagram.paint();
	}
	public import(diagram: Diagram): void {
		this.diagram.data.parse(diagram.data.serialize());
	}
	public parse(data: IDataItem[]): void {
		this.diagram.data.parse(data);
	}
	public serialize(): IDataItem[] {
		return this.diagram.data.serialize();
	}
	public getRootView(): VNode {
		return this._layout.getRootView();
	}
	public zoomIn(step = 0.05): void {
		this.config.scale = this.diagram.config.scale += step;
		this._layout.getCell("top").attach(topbar, this);
	}
	public zoomOut(step = 0.05): void {
		this.config.scale = this.diagram.config.scale =
			this.config.scale <= step ? step : this.config.scale - step;
		this._layout.getCell("top").attach(topbar, this);
	}
	public setViewMode(mode: ViewMode): void {
		this.config.editMode = mode === "edit";
		this._checkEditMode();
		this._layout.getCell("top").attach(topbar, this);
	}
	protected abstract _initUI(container);
	protected abstract _initDiagram();
	protected _setHandlers() {
		this._initHotkeys();
		this.diagram.events.on(DataEvents.change, () => this.paint());
		this.diagram.events.on(DiagramEvents.emptyAreaMouseDown, (event: MouseEvent) => {
			if (this.diagram.findNearestConnector(event)?.id !== this.diagram.selection.getId()) {
				this.diagram.selection.remove();
				this._sidebar.empty();
			}

			this._blockSelection.clearSelection();
			this._resizer.connect.removeNearShape();

			if (event.button !== 0) {
				return;
			}

			const container = this.diagram.getRootView().node.el as HTMLElement;
			const startCoords = this._blockSelection.getCurrentCoordinates(event, container);
			this._selectionBox = new SelectionBox(startCoords);
			drag.start(
				event,
				{
					onMove: (event: MouseEvent) => {
						this._selectionBox.end = this._blockSelection.getCurrentCoordinates(event, container);
						this.events.fire(DiagramEditorEvents.blockSelectionAreaChanged, [
							{
								start: this._selectionBox.start,
								end: this._selectionBox.end,
							},
						]);
						this.paint();
					},
					onUp: () => {
						if (this._selectionBox.isValid()) {
							this.events.fire(DiagramEditorEvents.blockSelectionFinished, [
								{ start: this._selectionBox.start, end: this._selectionBox.end },
								event.shiftKey ? "add" : event.altKey ? "remove" : "create",
							]);
						} else {
							this._blockSelection.clearSelection();
						}
						this._selectionBox = null;
						this.paint();
					},
				},
				null
			);
			this.paint();
		});
		this.diagram.events.on(DiagramEvents.itemClick, (id, event) => {
			this._itemsHandlerClick(id, event);
		});
		this.events.on(
			DiagramEditorEvents.blockSelectionFinished,
			(selection: ISelectionBox, mode: "add" | "remove" | "create") => {
				this._blockSelection.updateSelection(selection, mode);
			}
		);
		this.events.on(DiagramEditorEvents.zoomIn, () => {
			this.zoomIn();
		});
		this.events.on(DiagramEditorEvents.zoomOut, () => {
			this.zoomOut();
		});
		this.events.on(DiagramEditorEvents.visibility, () => {
			this.setViewMode(this.config.editMode ? "preview" : "edit");
		});
		// eslint-disable-next-line @typescript-eslint/unbound-method
		this.events.on(DiagramEditorEvents.importData, this._importFile);
		awaitRedraw().then(() => {
			this.getRootView().node.el.addEventListener("wheel", e => {
				if (e.ctrlKey || e.metaKey) {
					const delta = e.deltaY || e.detail || e.wheelDelta;
					if (delta > 0) {
						this.events.fire(DiagramEditorEvents.zoomOut);
					} else {
						this.events.fire(DiagramEditorEvents.zoomIn);
					}
					e.preventDefault ? e.preventDefault() : (e.returnValue = false);
				}
			});
		});
	}
	protected _checkEditMode() {
		if (this._layout.getCell("left")) {
			this._layout.getCell("left").config.hidden = !this.config.editMode;
		}
		this._layout.getCell("right").config.hidden = !this.config.editMode;

		const center = this._layout.getCell("center");

		if (this.config.editMode) {
			center.attach(this.diagram);
		}
		if (this.config.editMode) {
			center.config.css = center.config.css.replace(" dhx_preview_mode", "");
		}
		if (!this.config.editMode && center.config.css.indexOf(" dhx_preview_mode") === -1) {
			center.config.css += " dhx_preview_mode";
		}
	}
	protected _removeItem(id: Id) {
		const item = this.diagram.data.getItem(id);
		if (this.config.type !== "default" && item.$level === 0) return;

		this.diagram.selection.remove(id);
		this.diagram.data.remove(id);

		this._sidebar.empty();
		this._resizer.onUp();
	}
	protected _initHotkeys() {
		this._keyManager.addHotKey("delete", () => {
			if (!this._sidebar.isItemsSelected()) {
				this._removeSelected();
			}
		});
		this._keyManager.addHotKey("ctrl+z", () => {
			if (!this._sidebar.isItemsSelected()) {
				this.history.undo();
			}
		});
		this._keyManager.addHotKey("ctrl+shift+z", () => {
			if (!this._sidebar.isItemsSelected()) {
				this.history.redo();
			}
		});
		this._keyManager.addHotKey("arrowLeft", () => {
			if (!this._sidebar.isItemsSelected()) {
				const item = this.diagram.selection.getItem();
				if (item) {
					this._moveItem(item, this.config.gridStep, "left");
				} else {
					this._blockSelection.getSelected().forEach(selectedId => {
						const selectedItem = this.diagram.data.getItem(selectedId);
						if (!selectedItem.$group) this._moveItem(selectedItem, this.config.gridStep, "left");
					});
				}
			}
		});
		this._keyManager.addHotKey("arrowRight", () => {
			if (!this._sidebar.isItemsSelected()) {
				const item = this.diagram.selection.getItem();
				if (item) {
					this._moveItem(item, this.config.gridStep, "right");
				} else {
					this._blockSelection.getSelected().forEach(selectedId => {
						const selectedItem = this.diagram.data.getItem(selectedId);
						if (!selectedItem.$group) this._moveItem(selectedItem, this.config.gridStep, "right");
					});
				}
			}
		});
		this._keyManager.addHotKey("arrowUp", () => {
			if (!this._sidebar.isItemsSelected()) {
				const item = this.diagram.selection.getItem();
				if (item) {
					this._moveItem(item, this.config.gridStep, "up");
				} else {
					this._blockSelection.getSelected().forEach(selectedId => {
						const selectedItem = this.diagram.data.getItem(selectedId);
						if (!selectedItem.$group) this._moveItem(selectedItem, this.config.gridStep, "up");
					});
				}
			}
		});
		this._keyManager.addHotKey("arrowDown", () => {
			if (!this._sidebar.isItemsSelected()) {
				const item = this.diagram.selection.getItem();
				if (item) {
					this._moveItem(item, this.config.gridStep, "down");
				} else {
					this._blockSelection.getSelected().forEach(selectedId => {
						const selectedItem = this.diagram.data.getItem(selectedId);
						if (!selectedItem.$group) this._moveItem(selectedItem, this.config.gridStep, "down");
					});
				}
			}
		});
	}
	protected _getDefaults() {
		let defaults = {};
		const baseShape = [...Object.keys(flowShapes)];

		baseShape.forEach(type => {
			defaults[type] = {
				text: type[0].toUpperCase() + type.slice(1),
				fill: "#EEF1F6",
				stroke: "#B8C6D6",
				extraLinesStroke: "#B8C6D6",
			};
		});

		defaults = { ...defaults, text: { text: "Text" } };

		["card", "img-card", "topic"].forEach(type => {
			defaults[type] = {
				text: "Text",
				title: "Title",
				headerColor: "#03A9F4",
				stroke: "#03A9F4",
				height: type !== "topic" ? 90 : 30,
				width: type === "img-card" ? 210 : 140,
			};
		});

		return { ...defaults, ...this.config.defaults };
	}
	protected _importFile(data) {
		this.parse(data);
	}
	protected _getToolbarConfig({
		type,
		shapeToolbar,
	}: IOrgEditorConfig | IMindmapEditorConfig | IDefaultEditorConfig): IShapeToolbarConfig[] {
		const currentToolbar: IShapeToolbarConfig[] = [];
		switch (type) {
			case "mindmap":
				currentToolbar.push(...mindmapShapeToolbar);
				break;
			case "org":
				currentToolbar.push(...orgShapeToolbar);
				break;
			default:
				currentToolbar.push(...defaultShapeToolbar);
				break;
		}
		if (typeof shapeToolbar === "boolean") {
			if (shapeToolbar) {
				return currentToolbar;
			}
		}
		if (Array.isArray(shapeToolbar) && shapeToolbar.length) {
			const toolbarConfig: IShapeToolbarConfig[] = [];
			shapeToolbar.forEach(control => {
				if (typeof control === "boolean" && control) {
					toolbarConfig.push(...currentToolbar);
				}
				const toolbarControl = currentToolbar.find(({ id }) => id === control);
				if (typeof control === "string" && toolbarControl) {
					toolbarConfig.push(toolbarControl);
				}
				if (
					isObject(control) &&
					typeof control?.id === "string" &&
					typeof control?.content === "string"
				) {
					toolbarConfig.push(control);
				}
			});
			return toolbarConfig;
		}
		return [];
	}
	protected _moveItem(item: IDataItem, step: number, dir: ActionDirection): void {
		if (arguments.length < 3 || !item) return;

		const { $item, id, x, y, dx = 0, dy = 0 } = item;
		const type = $item.getBaseType();

		if (type === "group" || type === "swimlane") {
			this.diagram.data.eachChild(id, children => {
				switch (dir) {
					case "left":
						if (this.config.type === "default") {
							children.x = children.x - step;
						} else {
							children.dx = (children.dx || 0) - step;
						}
						break;
					case "right":
						if (this.config.type === "default") {
							children.x = children.x + step;
						} else {
							children.dx = (children.dx || 0) + step;
						}
						break;
					case "up":
						if (this.config.type === "default") {
							children.y = children.y - step;
						} else {
							children.dy = (children.dy || 0) - step;
						}
						break;
					case "down":
						if (this.config.type === "default") {
							children.y = children.y + step;
						} else {
							children.dy = (children.dy || 0) + step;
						}
						break;
				}
			});
		}

		let data = {};

		switch (dir) {
			case "left":
				data = this.config.type === "default" ? { x: x - step } : { dx: dx - step };
				break;
			case "right":
				data = this.config.type === "default" ? { x: x + step } : { dx: dx + step };
				break;
			case "up":
				data = this.config.type === "default" ? { y: y - step } : { dy: dy - step };
				break;
			case "down":
				data = this.config.type === "default" ? { y: y + step } : { dy: dy + step };
				break;
		}

		this.diagram.data.update(id, data);
	}
	protected _removeLineTitle(item: IDataItem): void {
		if (!item) return;

		const titleIndex = item.title.text.findIndex(i => i.id === item.$subSelected);
		item.title.text.splice(titleIndex, 1);

		this.diagram.selection.add(item.id);
		this.diagram.data.update(item.id, item);

		this.paint();
	}
	private _removeSelected() {
		const item = this.diagram.selection.getItem();
		if (item?.id) {
			if (item.$item.getBaseType() === "line" && item.$subSelected) {
				this._removeLineTitle(item);
				return;
			}

			this._removeItem(item.id);
		} else if (this._blockSelection.getSelected().length) {
			const selected = this._blockSelection.getSelected();
			selected.forEach(shapeId => {
				this._removeItem(shapeId);
			});
		}
	}
	private _itemsHandlerClick(id, event) {
		const item = this.diagram.data.getItem(id);

		if (event.shiftKey && !item.$group) {
			const selected = this.diagram.selection.getItem();

			if (selected && !selected.$group) {
				this.diagram.selection.remove();
				this._blockSelection.add(selected.id);
				this._sidebar.empty();
			}
			this._blockSelection.add(id);
		} else if (event.altKey) {
			this.diagram.selection.remove(id);
			this._blockSelection.remove(id);
		} else {
			this._blockSelection.clearSelection();
		}
	}
}
